import { api } from './api';

const DB_NAME = 'kidee-local';
const STORE = 'outbox';
const VERSION = 1;

export interface OutboxItem {
  id: string;
  path: string;
  method: string;
  body: unknown;
  createdAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function enqueue(item: OutboxItem): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      const syncManager = (registration as ServiceWorkerRegistration & {
        sync?: { register(tag: string): Promise<void> };
      }).sync;
      await syncManager?.register('kidee-outbox');
    } catch {
      // App-level online event remains the cross-browser fallback.
    }
  }
}

export async function listOutbox(): Promise<OutboxItem[]> {
  const db = await openDb();
  const values = await new Promise<OutboxItem[]>((resolve, reject) => {
    const request = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
    request.onsuccess = () => resolve(request.result as OutboxItem[]);
    request.onerror = () => reject(request.error);
  });
  db.close();
  return values.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

async function remove(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function flushOutbox(): Promise<number> {
  if (!navigator.onLine) return 0;
  const items = await listOutbox();
  let flushed = 0;
  for (const item of items) {
    try {
      await api(item.path, { method: item.method, body: item.body });
      await remove(item.id);
      flushed += 1;
    } catch (error) {
      const status = typeof error === 'object' && error && 'status' in error
        ? Number((error as { status: number }).status)
        : 0;
      if (status >= 400 && status < 500 && status !== 401) {
        await remove(item.id);
      } else {
        break;
      }
    }
  }
  if (flushed > 0) window.dispatchEvent(new CustomEvent('kidee:outbox-flushed', { detail: flushed }));
  return flushed;
}
